import {
  IconLink,
  OwnUpBodyMedium,
  OwnUpGridContainer,
  OwnUpGridItem,
  OwnUpGridOffset,
  OwnUpGridWrapper,
  OwnUpSmallBody
} from '@rategravity/own-up-component-library';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { getSrc } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import { kebab } from '../../../module/kebab';
import { PostSnippet } from '../../../types';
import { FeaturePosts } from '../../home/feature-posts';
import CTAButtons2 from '../ctaButton2';
import CTAButtons3 from '../ctaButton3';
import FastLinks from '../fast-links';
import SocialLinks from '../social-links';
import {
  addInteractiveBlocksToArticle,
  AuthorWrapper,
  BlogPostWrapper,
  calculateBodyWordsCount,
  CategoriesWrapper,
  CategoryGuidesContainer,
  CategoryGuidesItem,
  CategoryGuidesWrapper,
  DateWrapper,
  Headline,
  ImageWrapper,
  Link,
  MetadataInnerWrapper,
  MetadataWrapper,
  TooltipAuthorAvatar,
  TooltipAuthorDescription,
  TooltipAuthorDesignation,
  TooltipAuthorName,
  TooltipContainer,
  TooltipContent,
  TooltipContentBottomWrapper,
  TooltipContentTopWrapper,
  TooltipNameDesWrapper,
  TooltipText
} from './components';

export type BlogPostProps = {
  id: string;
  title: string;
  href: string;
  authors: AuthorProps[];
  factCheckers: AuthorProps[];
  tags: string[];
  img: IGatsbyImageData;
  imgAlt?: string;
  body: Record<string, any>[];
  bodyUpdatedAt: Date;
  relatedPostsData?: PostSnippet[];
};

export type AuthorProps = {
  name: string;
  designation: string;
  description: string;
  role: string[];
  mainImage: any;
  _rawBio: any;
  slug: {
    current: string;
  };
};

const cardStyles = {
  textAlign: 'left',
  backgroundColor: 'none',
  padding: '16px 0px',
  boxShadow: 'none',
  borderRadius: '8px'
};

const Tooltip = ({
  author,
  children,
  type
}: {
  children: JSX.Element;
  author: AuthorProps;
  type: string;
}) => {
  const { name, designation, description, mainImage } = author;

  const imgUrl = getSrc(mainImage?.asset);

  const [isVisible, setIsVisible] = useState(false);

  const handleTooltipEnter = () => {
    setIsVisible(true);
  };

  const handleTooltipLeave = () => {
    setIsVisible(false);
  };

  return (
    <TooltipContainer onMouseEnter={handleTooltipEnter} onMouseLeave={handleTooltipLeave}>
      <TooltipText>{children}</TooltipText>
      <TooltipContent visible={isVisible}>
        <TooltipContentTopWrapper>
          <TooltipAuthorAvatar src={imgUrl} alt={name} />
          <TooltipNameDesWrapper>
            {name && (
              <Link to={authorUrl(author, type)} key={1}>
                <TooltipAuthorName variant="h3">{name}</TooltipAuthorName>
              </Link>
            )}
            {designation && (
              <TooltipAuthorDesignation variant="body1">{designation}</TooltipAuthorDesignation>
            )}
          </TooltipNameDesWrapper>
        </TooltipContentTopWrapper>
        <TooltipContentBottomWrapper>
          {description && (
            <TooltipAuthorDescription variant="body1">{description}</TooltipAuthorDescription>
          )}
          <Link to={authorUrl(author, type)} key={1}>
            <TooltipAuthorDesignation variant="body1">See full bio</TooltipAuthorDesignation>
          </Link>
        </TooltipContentBottomWrapper>
      </TooltipContent>
    </TooltipContainer>
  );
};

const BlogDate = ({ bodyUpdatedAt }: { bodyUpdatedAt: Date }) => {
  const [date, setDate] = useState<string | null>(null);

  useEffect(() => {
    const inputDate = new Date(bodyUpdatedAt);
    const formattedDate = inputDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
    setDate(formattedDate);
  }, []);
  return date ? (
    <DateWrapper>
      <OwnUpSmallBody variant="body1">{date}</OwnUpSmallBody>
    </DateWrapper>
  ) : null;
};

const Author = ({ author, type }: { author: AuthorProps; type: string }) => {
  return (
    <Tooltip author={author} type={type}>
      <AuthorWrapper>
        <OwnUpSmallBody variant="body1">
          {`${type === 'author' ? 'Written' : type === 'fact_checker' ? 'Fact Checked' : ''}`}{' '}
          by:&nbsp;&nbsp;
        </OwnUpSmallBody>
        <Link to={authorUrl(author, type)} key={1}>
          <OwnUpSmallBody variant="body1">{author?.name}</OwnUpSmallBody>
        </Link>
      </AuthorWrapper>
    </Tooltip>
  );
};

export const authorUrl = (author: AuthorProps, type: string) => {
  return `/${type === 'author' ? 'author' : type === 'fact_checker' ? 'fact-checker' : ''}/${
    author?.slug?.current || ''
  }/`;
};

export const CategoryGuides = () => {
  const categories = [
    { name: 'First Home Loan', href: '/guide-buying-first-home/' },
    {
      name: 'Mortgage Advisors',
      href: '/how-to-find-mortgage-advisor/'
    },
    {
      name: 'Mortgage Comparison',
      href: '/guide-to-mortgage-rate-comparison-shopping/'
    }
  ];
  return (
    <OwnUpGridWrapper>
      <CategoryGuidesContainer>
        <CategoryGuidesWrapper>
          {categories?.map((cat, index) => (
            <>
              {index === 0 && (
                <OwnUpBodyMedium variant="body1">Guides:&nbsp;&nbsp;</OwnUpBodyMedium>
              )}
              <CategoryGuidesItem key={index}>
                <IconLink link={cat.href} component={Link} to={cat.href} icon={<></>}>
                  {cat.name}
                </IconLink>
              </CategoryGuidesItem>
            </>
          ))}
        </CategoryGuidesWrapper>
      </CategoryGuidesContainer>
    </OwnUpGridWrapper>
  );
};

export const BlogPost = ({
  id,
  title,
  href,
  authors,
  factCheckers,
  tags = [],
  img,
  imgAlt,
  bodyUpdatedAt,
  body,
  relatedPostsData
}: BlogPostProps) => {
  const renderedImg = img ? (
    <GatsbyImage image={img} alt={imgAlt || title} style={{ margin: 'auto', display: 'block' }} />
  ) : null;
  const wordsCount = calculateBodyWordsCount(body);
  const bodyArray = addInteractiveBlocksToArticle(body, href, wordsCount);
  const imageUrl = getSrc(img);

  return (
    <React.Fragment>
      <OwnUpGridWrapper>
        <OwnUpGridContainer variant="slim">
          <OwnUpGridItem xs>
            <Headline variant="h1">{title}</Headline>
            <MetadataWrapper>
              <MetadataInnerWrapper>
                <CategoriesWrapper>
                  {tags.map((tag, index) => (
                    <Link to={`/${kebab(tag)}/`} key={index}>
                      <OwnUpSmallBody variant="body1">{tag}</OwnUpSmallBody>
                    </Link>
                  ))}
                </CategoriesWrapper>
                {authors && authors?.length > 0 && authors[0]?.name && (
                  <Author author={authors[0]} type="author" />
                )}
                {factCheckers && factCheckers?.length > 0 && factCheckers[0]?.name && (
                  <Author author={factCheckers[0]} type="fact_checker" />
                )}
                <BlogDate bodyUpdatedAt={bodyUpdatedAt} />
              </MetadataInnerWrapper>
            </MetadataWrapper>
            <SocialLinks href={href} imageUrl={imageUrl} title={title} />
          </OwnUpGridItem>
        </OwnUpGridContainer>
      </OwnUpGridWrapper>
      <ImageWrapper>{renderedImg}</ImageWrapper>
      <OwnUpGridWrapper>
        <OwnUpGridContainer variant="slim">
          <FastLinks blocks={body} wordsCount={wordsCount} />
          <BlogPostWrapper xs={12} md={12} lg={7}>
            {bodyArray.map((obj: any) => {
              return obj.comp;
            })}
          </BlogPostWrapper>
          <OwnUpGridItem xs={0} sm={0} md={0} lg={3}>
            <CTAButtons3 />
          </OwnUpGridItem>
        </OwnUpGridContainer>
      </OwnUpGridWrapper>
      <OwnUpGridWrapper>
        <OwnUpGridContainer spacing={0} variant="slim">
          <OwnUpGridOffset sm={0} lg={2} />
          <OwnUpGridItem sm={12} lg={7}>
            <CTAButtons2 />
          </OwnUpGridItem>
          <OwnUpGridOffset sm={0} lg={3} />
        </OwnUpGridContainer>
      </OwnUpGridWrapper>
      <OwnUpGridWrapper>
        <OwnUpGridContainer variant="slim">
          <OwnUpGridItem xs={12}>
            <CategoryGuides />
          </OwnUpGridItem>
        </OwnUpGridContainer>
      </OwnUpGridWrapper>
      <FeaturePosts
        title="Related Articles"
        featurePosts={relatedPostsData}
        cardStyles={cardStyles}
      />
    </React.Fragment>
  );
};
